import React, { useRef, useState } from 'react';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Download, Lock, Loader2, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { logFirebaseEvent, trackResourceDownload, trackSignupIntent } from '@/lib/tracking';
import { generateTemplatePDF } from '@/lib/pdfGenerator';
import { templates, TemplateContent } from '@/lib/content/resources/templates';
import { toast } from 'sonner';

const TemplatesPage: React.FC = () => {
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [downloadReady, setDownloadReady] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const downloadInProgress = useRef(false);
  
  const appSignUpUrl = import.meta.env.VITE_APP_URL 
    ? `${import.meta.env.VITE_APP_URL}/auth?mode=signup`
    : '/auth?mode=signup';

  const sampleUrl = '/demo/sample-inspection';
  const trackSample = (source: string) => {
    void logFirebaseEvent('sample_inspection_intent', { source });
  };

  const handleDownload = async (t: TemplateContent) => {
      if (downloadInProgress.current || t.locked || t.format !== 'PDF') return;
      downloadInProgress.current = true;
      setDownloadReady(false);
      setDownloadError(false);
      void logFirebaseEvent('template_download_attempt', { template_id: t.id });
      
      if (t.format === 'PDF') {
          setGeneratingId(t.id);
          const toastId = toast.loading("Generating PDF template...");
          try {
              await generateTemplatePDF(t);
              trackResourceDownload(t.title, t.format);
              void logFirebaseEvent('template_pdf_generated', { template_id: t.id });
              setDownloadReady(true);
              toast.success("Download started!", { id: toastId });
          } catch (e) {
              console.error(e);
              setDownloadError(true);
              void logFirebaseEvent('template_download_failed', { template_id: t.id });
              toast.error("Failed to generate PDF. Please try again.", { id: toastId });
          } finally {
              setGeneratingId(null);
              downloadInProgress.current = false;
          }
      }
  };

  return (
    <div className="page-shell">
      <SEO 
        title="Free SWPPP Templates & Inspection Checklists"
        description="Download free printable SWPPP inspection forms and corrective action logs. No signup required. Try a sample digital inspection before creating an account."
        canonical="/resources/templates"
      />

      <div className="page-intro">
        <div className="container relative z-10 mx-auto max-w-3xl px-4 text-center">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: 'Free Templates', href: '/resources/templates' }
           ]} />

           <div className="eyebrow mb-6">
             <FileText size={15} /> Ready-to-use forms
           </div>
           <h1 className="mb-4 text-4xl font-black tracking-[-0.04em] md:text-6xl">
             Free SWPPP Inspection Templates
           </h1>
           <p className="text-xl text-muted-foreground">
             Download printable PDF forms below, with no signup required. Want to try digital reporting first?
           </p>
           <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
             <Button asChild><a href={sampleUrl} onClick={() => trackSample('templates_intro')}>Try a sample inspection <ArrowRight size={16} className="ml-2" /></a></Button>
             <Button variant="outline" asChild><a href={appSignUpUrl} onClick={() => trackSignupIntent('templates_intro')}>Create a free account</a></Button>
           </div>
           <p className="mt-3 text-sm text-muted-foreground">The sample needs no account. Free accounts include one active site; paid plans add capacity.</p>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-5xl mx-auto">
        {downloadError && <p role="alert" className="mb-6 rounded-lg border border-destructive p-4">Failed to generate PDF. Please try again.</p>}
        {downloadReady && (
          <div role="status" className="mb-8 rounded-xl border border-primary/30 bg-primary/5 p-6">
            <h2 className="text-xl font-bold">Your PDF is ready. Try the digital workflow next.</h2>
            <p className="my-3 text-muted-foreground">Look for the file in your browser downloads. Explore a sample inspection before creating an account.</p>
            <Button asChild><a href={sampleUrl} onClick={() => trackSample('templates_after_download')}>Try a sample inspection <ArrowRight size={16} className="ml-2" /></a></Button>
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
            {templates.filter(t => !t.locked && t.format === 'PDF').map((t) => (
                <Card key={t.id} className="flex flex-col border-border hover:border-primary/50 transition-colors">
                    <CardHeader>
                        <CardTitle className="text-lg">{t.title}</CardTitle>
                        <CardDescription>{t.desc}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow">
                        <div className="h-32 bg-muted/50 rounded-md border border-dashed border-border flex items-center justify-center relative overflow-hidden group">
                            <div className="w-20 h-24 bg-background shadow-sm border border-border/50 mx-auto transform group-hover:-translate-y-1 transition-transform">
                                <div className="space-y-1.5 p-2">
                                    <div className="h-1.5 w-12 bg-muted-foreground/20 rounded"></div>
                                    <div className="h-1.5 w-8 bg-muted-foreground/20 rounded"></div>
                                    <div className="h-1.5 w-14 bg-muted-foreground/20 rounded mt-2"></div>
                                    <div className="h-1.5 w-14 bg-muted-foreground/20 rounded"></div>
                                </div>
                            </div>
                            {t.locked && (
                                <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
                                    <Lock className="text-muted-foreground" />
                                </div>
                            )}
                        </div>
                    </CardContent>
                    <CardFooter>
                        {t.locked ? (
                            <Button variant="outline" className="w-full gap-2" asChild>
                                <a href={appSignUpUrl} onClick={() => trackSignupIntent('templates_locked_card')}>Unlock Free Account</a>
                            </Button>
                        ) : (
                            <Button 
                                variant="secondary" 
                                className="w-full gap-2"
                                onClick={() => handleDownload(t)}
                                disabled={generatingId !== null}
                            >
                                {generatingId === t.id ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Download size={16} />
                                )}
                                {generatingId === t.id ? 'Generating...' : `Download ${t.format}`}
                            </Button>
                        )}
                    </CardFooter>
                </Card>
            ))}
        </div>

        {/* SEO CONTENT SECTION */}
        <div className="prose dark:prose-invert max-w-3xl mx-auto pt-8 border-t border-border">
            <h2>Use these forms as a starting point</h2>
            <p>
                These general-purpose templates help organize site observations and follow-up work. They are not a complete SWPPP or a guarantee of compliance. Check your applicable permit and site-specific requirements before using them.
            </p>

            <h3>Information to prepare for an inspection</h3>
            <p>Have these details ready and add any fields your project requires:</p>
            <ul>
                <li><strong>Inspection Date:</strong> Month, day, and year.</li>
                <li><strong>Inspector Details:</strong> Name, role, and relevant qualifications.</li>
                <li><strong>Weather Information:</strong> Current weather and total rainfall since the last inspection.</li>
                <li><strong>BMP Status:</strong> A description of the condition of all BMPs implemented at the site.</li>
                <li><strong>Corrective Actions:</strong> A description of any issues found and the specific repairs needed.</li>
            </ul>

            <h3>The Risk of Paper Forms</h3>
            <p>
                A consistent digital workflow can help with common paper-record problems:
            </p>
            <ol>
                <li>They get lost in truck dashboards or rain-soaked binders.</li>
                <li>Handwriting can be illegible.</li>
                <li>Photos and follow-up notes can become separated from the original report.</li>
            </ol>
            
            <div className="not-prose mt-8 p-6 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900">
                <h4 className="font-bold text-lg mb-2 text-blue-900 dark:text-blue-100">Upgrade to Digital Inspections</h4>
                <p className="text-blue-800 dark:text-blue-200 mb-4 text-sm">
                    Record inspection details, attach photos, and create signed PDF reports in RunOffTrack. Start with a sample, then create a free account for your first active site.
                </p>
                <Button size="sm" asChild>
                    <a href={sampleUrl} onClick={() => trackSample('templates_bottom_cta')}>Try a sample inspection <ArrowRight size={14} className="ml-1"/></a>
                </Button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
